import { useState, useEffect } from 'react';

/**
 * Related Proposals Component
 * Displays proposals related to the current one
 */
export function RelatedProposals({ relatedProposalIds, currentProposalId, onProposalClick }) {
  const [relatedProposals, setRelatedProposals] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!relatedProposalIds || relatedProposalIds.length === 0) {
      setRelatedProposals([]);
      return;
    }

    fetchRelatedProposals();
  }, [relatedProposalIds]);

  const fetchRelatedProposals = async () => {
    setLoading(true);

    try {
      // Fetch each related proposal
      const promises = relatedProposalIds.map(async (proposalId) => {
        const params = new URLSearchParams({
          action: 't24_get_proposal_details',
          nonce: window.t24Governance.nonce,
          proposal_id: proposalId
        });

        const response = await fetch(
          `${window.t24Governance.ajaxUrl}?${params.toString()}`,
          { method: 'GET', credentials: 'same-origin' }
        );

        const data = await response.json();
        return data.success ? data.data.proposal : null;
      });

      const proposals = await Promise.all(promises);
      setRelatedProposals(proposals.filter(p => p !== null));
    } catch (err) {
      console.error('Error fetching related proposals:', err);
      setRelatedProposals([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="t24-related-proposals loading">
        <h3>Related Proposals</h3>
        <div className="t24-loading-spinner"></div>
      </div>
    );
  }

  if (!relatedProposals || relatedProposals.length === 0) {
    return null;
  }

  return (
    <div className="t24-related-proposals">
      <h3>
        <span className="related-icon">🔗</span>
        Related Proposals ({relatedProposals.length})
      </h3>

      <div className="related-proposals-list">
        {relatedProposals.map((proposal) => (
          <div
            key={proposal.id}
            className="related-proposal-item"
            onClick={() => onProposalClick(proposal)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onProposalClick(proposal);
              }
            }}
          >
            <div className="related-proposal-header">
              <span className={`t24-proposal-category-badge ${proposal.category} small`}>
                {window.t24Governance.categories[proposal.category] || proposal.category}
              </span>
              <span className={`t24-status-badge ${proposal.status} small`}>
                {window.t24Governance.statuses[proposal.status] || proposal.status}
              </span>
            </div>

            <h4 className="related-proposal-title">{proposal.title}</h4>

            {proposal.description && (
              <p className="related-proposal-description">
                {proposal.description.length > 80
                  ? proposal.description.substring(0, 80) + '...'
                  : proposal.description}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
